import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';
import { updateUserDetail } from '../../redux/reducer';
import { getUserInformation } from '../../redux/selector';

/* GrowthOS — Personal Settings (design: growthos/Settings.jsx `Personal`).
   Identity strip + editable profile form. Wired to the logged-in user
   (redux userInformation, with localStorage / router-state fallbacks) and
   saved through the real NetAuth/UpdateUser API — the same endpoint User
   Management uses for firstName/lastName/email. Role & Profile are not
   self-editable (updateUser carries no such fields), so they're shown
   read-only. */

const Ic = ({ name, size = 16, className }) => {
  const P = {
    camera: (
      <>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h6l2 3h3a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="3.2" />
      </>
    ),
    mail: (
      <>
        <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
        <path d="m22 6-10 7L2 6" />
      </>
    ),
    chevD: <path d="M6 9l6 6 6-6" />,
    check: <path d="M20 6 9 17l-5-5" />,
  };
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {P[name]}
    </svg>
  );
};

const PersonalSettings = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);

  // logged-in user: redux → router state → localStorage
  const userInfo = useSelector(getUserInformation);
  const user = useMemo(() => {
    if (userInfo) return userInfo;
    if (location.state?.userData) return location.state.userData;
    try {
      return JSON.parse(localStorage.getItem('userInformation'));
    } catch {
      return null;
    }
  }, [userInfo, location.state]);

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [formEdited, setFormEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(null); // local-only preview (matches legacy behavior — not persisted)

  useEffect(() => {
    if (!user) return;
    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
    });
    setFormEdited(false);
    setPhoto(null);
  }, [user]);

  const roleName = user?.roles?.[0]?.roleName || user?.role || '';
  const profileName = user?.profile_name || '';
  const designation = user?.designationName || user?.designation || user?.title || '';
  const company = user?.companyName || user?.company || '';
  const subtitle = [designation, company].filter(Boolean).join(' at ');

  const initials =
    `${(form.firstName || '').charAt(0)}${(form.lastName || '').charAt(0)}`.toUpperCase() || 'U';

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormEdited(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB. Please upload a smaller image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setPhoto(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleCancel = () => {
    if (!user) {
      navigate(-1);
      return;
    }
    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
    });
    setPhoto(null);
    setFormEdited(false);
  };

  const canSave =
    formEdited && !!form.firstName.trim() && !!form.lastName.trim() && !!form.email.trim();

  const handleSave = () => {
    if (!canSave || saving) return;
    const uid = user?.userId || user?.id;
    if (!uid) {
      toast.error('Unable to update: user id not found.');
      return;
    }
    setSaving(true);
    // preserve the fields updateUser expects but that this screen doesn't edit
    const payload = {
      userId: uid,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phoneNumber: user?.phoneNumber ?? user?.mobile,
      empId: user?.empId,
      designationId: user?.designationId,
      auth_type: user?.auth_type,
      password: user?.password,
    };
    apiServices
      .updateUser(payload)
      .then((response) => {
        if (response.status === 200) {
          const updated = {
            ...user,
            firstName: payload.firstName,
            lastName: payload.lastName,
            email: payload.email,
          };
          localStorage.setItem('userInformation', JSON.stringify(updated));
          dispatch(updateUserDetail({ userInformation: updated }));
          setFormEdited(false);
          toast.success('Personal settings updated successfully!', { position: 'top-right' });
        } else {
          const messages = [];
          for (const key in response.errors || {}) {
            if (Object.prototype.hasOwnProperty.call(response.errors, key)) {
              messages.push(...response.errors[key]);
            }
          }
          if (messages.length) messages.forEach((m) => toast.error(m, { position: 'top-right' }));
          else toast.error('Failed to update personal settings.', { position: 'top-right' });
        }
      })
      .catch(() => toast.error('Failed to update personal settings.', { position: 'top-right' }))
      .finally(() => setSaving(false));
  };

  const body = (
      <div className={'gos-personal' + (embedded ? ' gos-personal-embedded' : '')}>
        <div className="gos-ps-head">
          <div>
            <div className="gos-ps-title">Personal Settings</div>
            <div className="gos-ps-sub">
              Your profile controls module access, setup customizations, and how you appear to your
              team.
            </div>
          </div>
        </div>

        <div className="gos-card gos-ps-card">
          {/* identity strip */}
          <div className="gos-ps-identity">
            <div className="gos-ps-avatar-wrap">
              {photo ? (
                <img className="gos-ps-avatar gos-ps-avatar-img" src={photo} alt="Profile" />
              ) : (
                <div className="gos-ps-avatar">{initials}</div>
              )}
              <button
                type="button"
                className="gos-ps-avatar-edit"
                title="Change photo"
                onClick={() => fileRef.current?.click()}
              >
                <Ic name="camera" size={14} />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
            <div className="gos-ps-identity-meta">
              <div className="gos-ps-idrow">
                <span className="gos-ps-name">
                  {`${form.firstName} ${form.lastName}`.trim() || '—'}
                </span>
                {roleName ? <span className="gos-ps-role-badge">{roleName}</span> : null}
              </div>
              {subtitle ? <div className="gos-ps-subtitle">{subtitle}</div> : null}
              {form.email ? (
                <div className="gos-ps-email-line">
                  <Ic name="mail" size={13} />
                  {form.email}
                </div>
              ) : null}
            </div>
          </div>

          <div className="gos-ps-divider" />

          {/* form */}
          <div className="gos-ps-form">
            <div className="gos-ps-field">
              <label className="gos-ps-label">
                First Name<span className="gos-ps-req"> *</span>
              </label>
              <input
                className="gos-ps-input"
                type="text"
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
              />
            </div>
            <div className="gos-ps-field">
              <label className="gos-ps-label">
                Last Name<span className="gos-ps-req"> *</span>
              </label>
              <input
                className="gos-ps-input"
                type="text"
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
              />
            </div>
            <div className="gos-ps-field">
              <label className="gos-ps-label">
                Email<span className="gos-ps-req"> *</span>
              </label>
              <input
                className="gos-ps-input"
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
              />
            </div>
            <div className="gos-ps-field">
              <label className="gos-ps-label">Role</label>
              <div className="gos-ps-select-wrap">
                <select
                  className="gos-ps-input gos-ps-select"
                  value={roleName}
                  disabled
                  title="Managed by your administrator"
                >
                  <option>{roleName || '—'}</option>
                </select>
                <Ic name="chevD" size={16} className="gos-ps-select-caret" />
              </div>
            </div>
            <div className="gos-ps-field wide">
              <label className="gos-ps-label">Profile</label>
              <div className="gos-ps-select-wrap">
                <select
                  className="gos-ps-input gos-ps-select"
                  value={profileName}
                  disabled
                  title="Managed by your administrator"
                >
                  <option>{profileName || '—'}</option>
                </select>
                <Ic name="chevD" size={16} className="gos-ps-select-caret" />
              </div>
            </div>
          </div>
        </div>

        <div className="gos-ps-actions">
          <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="gos-btn gos-btn-primary"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            <Ic name="check" size={16} />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
  );

  return embedded ? body : <div className="inner-page">{body}</div>;
};

export default PersonalSettings;
